import { SpeedRunButton, useInSpeedRun } from '@/components/engagement/SpeedRunBar';
import { confirmLeave } from '@/hooks/useUnsavedGuard';
import { useUiMode } from "@/hooks/useUiMode";
import { placeInMode } from "@/lib/uiMode.core";
import { UiModeSwitch } from "@/components/UiModeSwitch";
import { track } from "@/lib/analytics";
import { CONTACT_LINKS, SOCIAL_LINKS } from "@/lib/social";
import { CaretRight, EnvelopeSimple, InstagramLogo, TelegramLogo, TiktokLogo, WhatsappLogo } from "@phosphor-icons/react";
import { useState } from "react";
import { Coins,
  House,
  Users,
  Sliders,
  Lightning,
  BookOpen,
  ChartBar,
  Sparkle,
  UsersThree,
} from "@phosphor-icons/react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogoOrb } from "@/components/LogoOrb";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { openUpgrade } from "@/lib/upgrade";
import { LockedBadge } from "@/components/ui/locked";
import type { Feature } from "@/lib/tiers";
import { motion } from "motion/react";
import { aiNavText } from "./aiNavText";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";

// `feature`: the tier feature behind the page (lib/tiers.ts). A page the tier
// does not open stays in the list with a lock: visible, not usable.
type NavItem = { title: string; url: string; icon: typeof House; tour?: string; feature?: Feature; also?: string[]; ai?: boolean };
/*
 * FOUR MAIN PLACES, FOUR SECONDARY. Guillaume, 2026-10-21 (engagement redesign):
 * Dashboard -> Traders -> Strategies (renamed from Strategy Builder) -> My Copies
 * (Copy Trade and My Sessions as one place); "Assets, Learn, AI Connections and
 * Community are secondary": a smaller group below, no "More" bucket.
 * `also`: other paths that belong to the same place, so the item stays lit.
 */
/* During the speed run My Copies opens New copy > Backtest, not the session list
   (Guillaume 11-03: "When I click on My Copies, it should bring me to the New Copy,
   Backtest section not My Sessions"). */
export function navTarget(url: string, inRun: boolean): string {
  return inRun && url === "/sessions" ? "/copy-trade?tab=backtest" : url;
}

const navItems: NavItem[] = [
  { title: "nav.dashboard", url: "/", icon: House, tour: "nav-dashboard" },
  { title: "nav.traders", url: "/traders", icon: Users, tour: "nav-traders" },
  { title: "nav.strategies", url: "/configure", icon: Sliders, tour: "nav-strategy" },
  { title: "nav.myCopies", url: "/sessions", icon: Lightning, tour: "nav-sessions", also: ["/copy-trade", "/session/"] },
];

// Community: open to everyone (Guillaume, 2026-10-05). A Regular feature, so a
// tier lens shows it locked below Regular; tiers are not switched on.
const secondaryItems: NavItem[] = [
  { title: "nav.assets", url: "/assets", icon: Coins },
  { title: "nav.learn", url: "/learn", icon: BookOpen, tour: "nav-learn" },
  // THE AI SECTION (Michika, 2026-10-22, merged from main): "AI" with
  // Connection and MiroFish under it and an orange glow so it stands out; its
  // words are in aiNavText.ts, MiroFish is listed for admins only. Kept in the
  // secondary group of the 4 + 4 menu (Guillaume 2026-10-21).
  { title: "nav.aiConnections", url: "/ai", icon: Sparkle, tour: "nav-ai", feature: "ai_advisor", ai: true },
  { title: "nav.community", url: "/community", icon: UsersThree, feature: "community", tour: "nav-community" },
];

// The glow is on the word and its icon, never a frame (Michika, 2026-10-22).
const AI_GLOW_ICON = "text-orange-400 drop-shadow-[0_0_6px_rgba(251,146,60,0.9)]";
const AI_GLOW_TEXT = "text-orange-300 [text-shadow:0_0_10px_rgba(251,146,60,0.8)]";

const NAV_ORDER = ["/", "/traders", "/assets", "/configure", "/ai", "/sessions", "/community", "/learn"];
const LITE_PLACES = ["/", "/traders", "/configure", "/sessions", "/learn"];

function isActiveItem(item: NavItem, path: string): boolean {
  if (item.url === "/") return path === "/";
  return [item.url, ...(item.also ?? [])].some((u) => path.startsWith(u));
}


/** Below Learn, and only for the team. Guillaume, 2026-09-04: "a section that is
 *  only accessible for admins where I can monitor the user's experience." */
const adminItems = [
  { title: "nav.activity", url: "/admin", icon: ChartBar },
];

export function TradingSidebar() {
  const { open } = useSidebar();
  const { isAdmin, isRealAdmin, can } = useAuth();
  const inRun = useInSpeedRun();
  // A locked entry opens the plans right away instead of a locked page (Guillaume 10-25).
  const lockedNav = (item: { feature?: Feature }) => {
    if (!item.feature || can(item.feature)) return false;
    openUpgrade(item.feature);
    return true;
  };
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const aiWords = aiNavText(i18n.language);
  // Lite leaves out Assets, AI and Community; Pro shows everything (lib/uiMode.core.ts).
  const { mode } = useUiMode();
  // Round 7 #109: Learn and AI open their page in one click, as on Lite; the page lists their parts.
  const subOf = (_item: { ai?: boolean; url: string }): { label: string; url: string }[] | null => null;
  // Learn and AI are not pages in Pro: a click opens their options under them, with a
  // chevron (Guillaume's /preview review #20, 10-24). Open while one of their pages is.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Every place is in the one section now (review #30, 10-25); the second group stays empty.
  const secondaryShown: NavItem[] = [];
  const isExpanded = (url: string) => expanded[url] ?? location.pathname.startsWith(url);

  return (
    <Sidebar className="border-e border-white/[0.06] glass-heavy">
      <SidebarHeader className="border-b border-white/[0.06] px-6 py-5">
        {/* The Orb beside the wordmark, the lockup the login page uses.
            Guillaume, 2026-09-25. */}
        <div className="flex items-center justify-center gap-2.5">
          <img
            src="/copyfi_logo.png"
            alt="CopyFi"
            className="h-8 object-contain"
          />
          <LogoOrb className="w-8 h-8" />
        </div>
      </SidebarHeader>

      <SidebarContent className="p-4">
        <SidebarGroup>
          <SidebarGroupLabel className="text-muted-foreground text-xs font-semibold uppercase tracking-wider mb-3">
            {t('nav.heading')}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {/* ONE section (Guillaume's /preview review #30, 10-25): the four main places,
                  then Learn, Assets, AI and Community, same white names, same size. Lite keeps
                  its five (Learn opens its page, #40 10-24). */}
              {/* Round 7 #112 (Guillaume 10-26): Dashboard, Traders, Assets, Strategies, AI,
                  My Copies, Community, Learn. Lite keeps the ones it has, in that order. */}
              {NAV_ORDER.map((u) => [...navItems, ...secondaryItems].find((i) => i.url === u)!)
                .filter((i) => i && (mode === "lite" ? LITE_PLACES.includes(i.url) : placeInMode(i.url, mode)))
                .map((item) => {
                const isActive = isActiveItem(item, location.pathname);

                const Icon = item.icon;

                return (
                  <SidebarMenuItem key={item.title}>
                    {subOf(item) && mode === "pro" ? (
                      <SidebarMenuButton
                        type="button"
                        data-tour={item.tour}
                        aria-expanded={isExpanded(item.url)}
                        onClick={() => { if (lockedNav(item)) return; setExpanded((e) => ({ ...e, [item.url]: !isExpanded(item.url) })); track('nav_clicked', item.url, undefined, { item: item.url, from: location.pathname, expand: true }); }}
                        className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${isActive ? "bg-primary/15" : ""}`}
                      >
                        <Icon className={`relative z-10 flex-shrink-0 ${item.ai ? AI_GLOW_ICON : ""}`} size={20} weight={isActive ? "fill" : "regular"} />
                        {open && <span className={`relative z-10 text-sm font-medium whitespace-nowrap ${item.ai ? AI_GLOW_TEXT : ""}`}>{item.ai ? aiWords.ai : t(item.title)}</span>}
                        {open && item.feature && <LockedBadge feature={item.feature} className="relative z-10 ms-auto" />}
                        {open && <CaretRight size={12} className={`${item.feature ? "" : "ms-auto "}relative z-10 shrink-0 transition-transform ${isExpanded(item.url) ? "rotate-90" : ""}`} />}
                      </SidebarMenuButton>
                    ) : (
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={navTarget(item.url, inRun)}
                        data-tour={item.tour}
                        /* Ask before discarding a half-built strategy. The
                           builder registers a guard; every other page registers
                           nothing and this is a no-op. See hooks/useUnsavedGuard. */
                        onClick={(e) => {
                          if (lockedNav(item)) { e.preventDefault(); return; }
                          if (!confirmLeave()) { e.preventDefault(); return; }
                          track('nav_clicked', item.url, undefined, { item: item.url, from: location.pathname });
                        }}
                        className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200 ${
                          isActive
                            ? "text-primary-foreground"
                            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        }`}
                      >
                        {isActive && (
                          <motion.div
                            layoutId="activeNavBg"
                            className="absolute inset-0 rounded-lg"
                            style={{
                              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.25), rgba(99, 102, 241, 0.15))',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                              boxShadow: '0 0 20px rgba(37, 99, 235, 0.15), inset 0 1px 0 rgba(255,255,255,0.1)',
                              backdropFilter: 'blur(10px)',
                            }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          />
                        )}

                        <Icon
                          className={`relative z-10 flex-shrink-0 ${item.ai ? AI_GLOW_ICON : ""}`}
                          size={20}
                          weight={isActive ? "fill" : "regular"}
                        />
                        {open && (
                          <span className={`relative z-10 text-sm font-medium whitespace-nowrap ${item.ai ? AI_GLOW_TEXT : ""}`}>{item.ai ? aiWords.ai : t(item.title)}</span>
                        )}
                        {open && item.feature && <LockedBadge feature={item.feature} className="relative z-10 ms-auto" />}
                      </NavLink>
                    </SidebarMenuButton>
                    )}
                    {/* Connection and MiroFish show once the AI section is open. */}
                    {subOf(item) && open && mode === "pro" && isExpanded(item.url) && (
                      <SidebarMenuSub>
                        {subOf(item)!.map((sub) => (
                          <SidebarMenuSubItem key={sub.url}>
                            <SidebarMenuSubButton asChild isActive={location.pathname.startsWith(sub.url)}>
                              <NavLink to={sub.url} end onClick={(e) => { if (!confirmLeave()) e.preventDefault(); }}>
                                <span>{sub.label}</span>
                              </NavLink>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {secondaryShown.length > 0 && <SidebarGroup className="mt-2 pt-3 border-t border-white/[0.06]">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-0.5">
              {secondaryShown.map((item) => {
                const Icon = item.icon;
                const isActive = isActiveItem(item, location.pathname);
                return (
                  <SidebarMenuItem key={item.title}>
                    {/* Pro: Learn and AI expand (no page of their own). Lite: Learn is its page (#40). */}
                    {subOf(item) && mode === "pro" ? (
                      <SidebarMenuButton
                        type="button"
                        data-tour={item.tour}
                        aria-expanded={isExpanded(item.url)}
                        onClick={() => { if (lockedNav(item)) return; setExpanded((e) => ({ ...e, [item.url]: !isExpanded(item.url) })); track('nav_clicked', item.url, undefined, { item: item.url, from: location.pathname, expand: true }); }}
                        className={`relative flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors duration-200 ${
                          isActive
                            ? "text-primary-foreground bg-primary/15"
                            : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        }`}
                      >
                        <Icon size={16} weight={isActive ? "fill" : "regular"} className={`shrink-0 ${item.ai ? AI_GLOW_ICON : ""}`} />
                        {open && <span className={`text-xs font-medium whitespace-nowrap ${item.ai ? AI_GLOW_TEXT : ""}`}>{item.ai ? aiWords.ai : t(item.title)}</span>}
                        {open && item.feature && <LockedBadge feature={item.feature} className="ms-auto" />}
                        {open && <CaretRight size={12} className={`${item.feature ? "" : "ms-auto "}shrink-0 transition-transform ${isExpanded(item.url) ? "rotate-90" : ""}`} />}
                      </SidebarMenuButton>
                    ) : (
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          data-tour={item.tour}
                          onClick={(e) => { if (lockedNav(item)) { e.preventDefault(); return; } if (!confirmLeave()) { e.preventDefault(); return; } track('nav_clicked', item.url, undefined, { item: item.url, from: location.pathname }); }}
                          className={`relative flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors duration-200 ${
                            isActive
                              ? "text-primary-foreground bg-primary/15"
                              : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                          }`}
                        >
                          <Icon size={16} weight={isActive ? "fill" : "regular"} className={`shrink-0 ${item.ai ? AI_GLOW_ICON : ""}`} />
                          {open && <span className={`text-xs font-medium whitespace-nowrap ${item.ai ? AI_GLOW_TEXT : ""}`}>{item.ai ? aiWords.ai : t(item.title)}</span>}
                          {open && item.feature && <LockedBadge feature={item.feature} className="ms-auto" />}
                        </NavLink>
                      </SidebarMenuButton>
                    )}
                    {subOf(item) && open && mode === "pro" && isExpanded(item.url) && (
                      <SidebarMenuSub>
                        {subOf(item)!.map((sub) => (
                          <SidebarMenuSubItem key={sub.url}>
                            <SidebarMenuSubButton asChild isActive={location.pathname.startsWith(sub.url)}>
                              <NavLink to={sub.url} end onClick={(e) => { if (!confirmLeave()) e.preventDefault(); }}>
                                <span>{sub.label}</span>
                              </NavLink>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>}

        {/* During the admin's speed run (the lens is Free, so isAdmin is false):
            the "Speed run" entry becomes the way out (Guillaume 11-01). */}
        {!isAdmin && isRealAdmin && inRun && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu className="space-y-1">
                <SidebarMenuItem>
                  <SpeedRunButton showLabel={open} className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-orange-300 hover:bg-sidebar-accent" />
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Admins only, and rendered only for them: a nav item that leads to
            "you cannot see this" is worse than no nav item. The page checks the
            role again itself, because a hidden link is not a permission. */}
        {isAdmin && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu className="space-y-1">
                {adminItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname.startsWith(item.url);
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <NavLink
                          to={item.url}
                          onClick={(e) => { if (!confirmLeave()) e.preventDefault(); }}
                          className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200 ${
                            isActive
                              ? "text-primary-foreground bg-primary/20"
                              : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                          }`}
                        >
                          <Icon size={18} weight={isActive ? "fill" : "regular"} className="shrink-0" />
                          {open && <span className="text-sm font-medium">{t(item.title)}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
                {/* See CopyFi as a newcomer, without a new account (live review #143). */}
                {isAdmin && open && (
                  <SidebarMenuItem>
                    <SpeedRunButton className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-orange-300 hover:bg-sidebar-accent" />
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-white/[0.06] p-4 space-y-3">
        {open && <UiModeSwitch compact />}
        {/* CopyFi's channels (lib/social.ts, 2026-10-21), named "Keep up" (review #5, 10-25). */}
        <div className="flex items-center justify-center gap-4">
          {open && <span className="text-xs text-muted-foreground">{t("social.keepUp")}</span>}
          {SOCIAL_LINKS.map((s) => {
            const Icon = s.key === "telegram" ? TelegramLogo : s.key === "tiktok" ? TiktokLogo : InstagramLogo;
            return (
              <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={t(`social.${s.key}`)}
                className="text-muted-foreground hover:text-foreground transition-colors">
                <Icon size={20} />
              </a>
            );
          })}
        </div>
        {/* Contact, for help or an emergency (review #4, 10-24). */}
        {open && (
          <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
            <span>{t("social.contact")}</span>
            {CONTACT_LINKS.map((c) => {
              const Icon = c.key === "email" ? EnvelopeSimple : c.key === "whatsapp" ? WhatsappLogo : TelegramLogo;
              return (
                <a key={c.key} href={c.url} target="_blank" rel="noopener noreferrer" aria-label={t(`social.${c.key}`)} title={t(`social.${c.key}`)}
                  className="hover:text-foreground transition-colors">
                  <Icon size={18} />
                </a>
              );
            })}
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
